import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge from './ui/StatusBadge';
import Button from './ui/Button';
import { useConfirm } from './ui/ConfirmProvider';
import { FormInput, FormSelect, FormTextarea } from './FormField';
import ProcSection from './procurement/ProcSection';
import { useFieldErrors, useFormSaveEvent, useClearErrorsOnEdit } from './forms/_shared';
import { useNotifications } from './NotificationProvider';
import { newIdempotencyKey } from './procurement/ReceivingPanel';
import ReadyToReceivePanel from './procurement/ReadyToReceivePanel';
import DataTable, { actionsColumn, Column } from './DataTable';
import RecordActivity, { statusChangeEvent } from './ui/RecordActivity';
import ShippingAgentComparison from './procurement/ShippingAgentComparison';
import { deliveryPrefill } from './procurement/shipmentAgentHelpers';
import { PROGRESS_VARIANT, SHIPMENT_LEG_OPTIONS, SHIPMENT_MODE_OPTIONS, SHIPMENT_STATUS_LABELS, SHIPMENT_TRANSITIONS } from './procurement/procurementLabels';
import { useLoad } from '../hooks/useLoad';
import { useRole } from '../hooks/useRole';
import { useShippingAgents } from '../hooks/useShippingAgents';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { PROC_EVENTS, setBarDetail } from '../hooks/useProcurementBar';
import {
  attachShipment, createShipment, detachShipment, fetchFreightAllocation, fetchOrders, fetchShipment, saveWaybill, updateShipment,
} from '../services/ProcurementService';
import { CURRENCY_OPTIONS } from '../utils/currency';
import { formatDecimal } from '../utils/decimal';
import { entityPath } from '../utils/entityRoutes';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { procurementErrorMessage, toSaveError, validationError } from '../utils/procurementErrors';
import type { ShipmentDetail, ShipmentInput, ShipmentLeg, ShippingMode } from '../types/Procurement';
import '../styles/components/ProcurementScreens.css';
import { recordLabelOr } from '../utils/recordId';
import { useRecordRefs } from '../utils/recordRefs';

type OrderOnShipment = ShipmentDetail['orders'][number];
type TrackingEntry = ShipmentDetail['tracking'][number];
type FreightShare = NonNullable<ReturnType<typeof fetchFreightAllocation> extends Promise<infer R> ? R : never>['shares'][number];

/** Progress stages that mean the goods are here, so what arrived can be confirmed. */
const ARRIVED_PROGRESS = ['Arrived', 'Notified', 'Received'];

/** The legs whose goods are counted in here. The China-side leg ends at the agent's warehouse, so there is nothing to confirm. */
const RECEIVABLE_LEGS: ReadonlyArray<ShipmentLeg | ''> = ['international', 'domestic-destination'];

export interface ShipmentForm {
  mode: string; leg: string; shipping_agent_id: string; container_ref: string; waybill_no: string; vessel_name: string; port_of_loading: string;
  load_date: string; actual_departure: string; estimated_arrival: string; actual_arrival: string; notified_date: string; received_date: string;
  weight_kg: string; total_cbm: string; total_cartons: string;
  freight_amount: string; freight_currency: string; freight_charged_date: string; local_clearing: string; local_clearing_currency: string;
  delivery_recipient: string; delivery_address: string; notes: string;
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const day = (v: unknown): string => str(v).slice(0, 10);

export const emptyShipmentForm = (): ShipmentForm => ({
  mode: '', leg: '', shipping_agent_id: '', container_ref: '', waybill_no: '', vessel_name: '', port_of_loading: '', load_date: '',
  actual_departure: '', estimated_arrival: '', actual_arrival: '', notified_date: '', received_date: '', weight_kg: '', total_cbm: '',
  total_cartons: '', freight_amount: '', freight_currency: '', freight_charged_date: '', local_clearing: '', local_clearing_currency: '',
  delivery_recipient: '', delivery_address: '', notes: '',
});

export const formFromShipment = (s: ShipmentDetail): ShipmentForm => ({
  mode: str(s.mode), leg: str(s.leg), shipping_agent_id: str(s.shipping_agent_id), container_ref: str(s.container_ref), waybill_no: str(s.waybill_no),
  vessel_name: str(s.vessel_name), port_of_loading: str(s.port_of_loading), load_date: day(s.load_date), actual_departure: day(s.actual_departure),
  estimated_arrival: day(s.estimated_arrival), actual_arrival: day(s.actual_arrival), notified_date: day(s.notified_date), received_date: day(s.received_date),
  weight_kg: str(s.weight_kg), total_cbm: str(s.total_cbm), total_cartons: str(s.total_cartons), freight_amount: str(s.freight_amount),
  freight_currency: str(s.freight_currency), freight_charged_date: day(s.freight_charged_date), local_clearing: str(s.local_clearing),
  local_clearing_currency: str(s.local_clearing_currency), delivery_recipient: str(s.delivery_recipient), delivery_address: str(s.delivery_address), notes: str(s.notes),
});

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** Money fields are only sent by someone who may see money (a contractor reads them as null; sending that back would wipe them). */
export function buildShipmentPayload(form: ShipmentForm, canViewFinancials: boolean): ShipmentInput {
  const body: ShipmentInput = {
    mode: (orNull(form.mode) as ShippingMode | null),
    leg: (orNull(form.leg) as ShipmentLeg | null),
    shipping_agent_id: orNull(form.shipping_agent_id),
    container_ref: orNull(form.container_ref),
    waybill_no: orNull(form.waybill_no),
    vessel_name: orNull(form.vessel_name),
    port_of_loading: orNull(form.port_of_loading),
    load_date: orNull(form.load_date),
    actual_departure: orNull(form.actual_departure),
    estimated_arrival: orNull(form.estimated_arrival),
    actual_arrival: orNull(form.actual_arrival),
    notified_date: orNull(form.notified_date),
    received_date: orNull(form.received_date),
    weight_kg: orNull(form.weight_kg),
    total_cbm: orNull(form.total_cbm),
    total_cartons: form.total_cartons.trim() === '' ? null : Number(form.total_cartons),
    delivery_recipient: orNull(form.delivery_recipient),
    delivery_address: orNull(form.delivery_address),
    notes: orNull(form.notes),
  };
  if (canViewFinancials) {
    body.freight_amount = orNull(form.freight_amount);
    body.freight_currency = orNull(form.freight_currency);
    body.freight_charged_date = orNull(form.freight_charged_date);
    body.local_clearing = orNull(form.local_clearing);
    body.local_clearing_currency = orNull(form.local_clearing_currency);
  }
  return body;
}

export function validateShipmentForm(form: ShipmentForm, canViewFinancials: boolean): Record<string, string> {
  const errors: Record<string, string> = {};
  if (canViewFinancials) {
    if (form.freight_amount.trim() && !form.freight_currency.trim()) errors.freight_currency = 'Choose the currency of the freight amount.';
    if (form.local_clearing.trim() && !form.local_clearing_currency.trim()) errors.local_clearing_currency = 'Choose the currency of the clearing amount.';
  }
  if (form.total_cartons.trim() && !/^\d+$/.test(form.total_cartons.trim())) errors.total_cartons = 'Enter a whole number.';
  return errors;
}

const ShipmentEdit: React.FC<{ shipmentId?: string }> = ({ shipmentId }) => {
  // Re-render when the record numbers load.
  useRecordRefs();
  const isNew = !shipmentId;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showSuccess, showError } = useNotifications();
  const { canEdit, canViewFinancials } = useRole();
  const agents = useShippingAgents();
  const load = useLoad<ShipmentDetail | null>(() => (shipmentId ? fetchShipment(shipmentId) : Promise.resolve(null)), [shipmentId]);
  const shipment = load.data;
  const [form, setForm] = useState<ShipmentForm>(emptyShipmentForm);
  const fieldErrors = useFieldErrors();
  const idempotencyKey = useRef(newIdempotencyKey());

  const freight = useLoad(
    () => (shipmentId && canViewFinancials ? fetchFreightAllocation(shipmentId) : Promise.resolve(null)),
    [shipmentId, canViewFinancials],
  );
  const attachable = useLoad(() => (canEdit && shipmentId ? fetchOrders({ lifecycle: 'active' }) : Promise.resolve([])), [canEdit, shipmentId]);
  const [pickOrder, setPickOrder] = useState('');
  const [cbm, setCbm] = useState('');
  const [cartons, setCartons] = useState('');
  const [trackingNo, setTrackingNo] = useState('');
  const [courier, setCourier] = useState('');

  useEffect(() => { if (shipment) setForm(formFromShipment(shipment)); }, [shipment]);

  // Unsaved edits: compared with what was loaded (or the blank form on a new shipment).
  const baseline = useMemo(() => (shipment ? formFromShipment(shipment) : emptyShipmentForm()), [shipment]);
  const dirty = canEdit && (isNew || shipment) ? JSON.stringify(form) !== JSON.stringify(baseline) : false;
  useUnsavedChangesGuard(dirty);

  useEffect(() => {
    if (!shipment) { setBarDetail(null); return undefined; }
    setBarDetail({
      kind: 'shipment', id: shipment.id, title: shipment.waybill_no || shipment.container_ref || `Shipment ${shipment.id.slice(0, 8).toUpperCase()}`,
      archived: shipment.status === 'cancelled', status: shipment.status,
    });
    return () => setBarDetail(null);
  }, [shipment]);

  const set = (key: keyof ShipmentForm) => (e: { target: { value: string } }) => setForm(f => ({ ...f, [key]: e.target.value }));
  // Choosing the agent or the China-side leg fills a blank delivery address from the agent's China warehouse.
  const setWithAddress = (key: 'shipping_agent_id' | 'leg') => (e: { target: { value: string } }) =>
    setForm(f => {
      const next = { ...f, [key]: e.target.value };
      const agent = agents.agents.find(a => a.id === next.shipping_agent_id);
      return { ...next, ...deliveryPrefill(agent, next.leg as ShipmentLeg | '', next.delivery_address) };
    });

  const refreshAll = useCallback(async () => { await Promise.all([load.reload(), freight.reload()]); }, [load, freight]);

  const run = async (action: () => Promise<unknown>, done: string | null, failed: string): Promise<boolean> => {
    try {
      await action();
      if (done) showSuccess(done);
      return true;
    } catch (err) {
      showError(procurementErrorMessage(err, failed));
      return false;
    }
  };

  // A failed save throws: useFormSaveEvent then shows the field errors under their fields (or a toast).
  const save = useCallback(async () => {
    if (!canEdit) return;
    const found = validateShipmentForm(form, canViewFinancials);
    if (Object.keys(found).length > 0) throw validationError(found);
    try {
      const body = buildShipmentPayload(form, canViewFinancials);
      if (isNew) {
        const created = await createShipment({ ...body, idempotency_key: idempotencyKey.current });
        showSuccess('Shipment created');
        navigate(entityPath('shipment', created.id) ?? '/procurement/shipments', { replace: true });
      } else {
        await updateShipment(shipmentId as string, body);
        showSuccess('Shipment saved');
        await refreshAll();
      }
    } catch (err) {
      throw toSaveError(err, 'The shipment could not be saved. Please try again.');
    }
  }, [canEdit, form, canViewFinancials, isNew, shipmentId, navigate, refreshAll, showSuccess]);

  // The breadcrumb bar's Save sends PROC_EVENTS.shipmentSave.
  useFormSaveEvent(save, { event: PROC_EVENTS.shipmentSave, fieldErrors, fallback: 'The shipment could not be saved. Please try again.' });
  useClearErrorsOnEdit({ ...form }, fieldErrors);

  const move = useCallback(async (to: 'draft' | 'active' | 'cancelled') => {
    if (!shipmentId || !shipment) return;
    if (!SHIPMENT_TRANSITIONS[shipment.status].includes(to)) { showError('That status change is not allowed from where this shipment is now.'); return; }
    if (to === 'cancelled') {
      const ok = await confirm({ title: 'Cancel this shipment?', description: 'Its freight stops counting toward the landed cost of the orders on it. You can reactivate it later.', confirmLabel: 'Cancel shipment', cancelLabel: 'Keep shipment', variant: 'danger' });
      if (!ok) return;
    }
    if (await run(() => updateShipment(shipmentId, { status: to }), to === 'cancelled' ? 'Shipment cancelled' : to === 'active' ? 'Shipment booked' : 'Shipment moved back to draft', 'The status could not be changed.')) await refreshAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId, shipment, confirm, refreshAll]);

  const latest = useRef({ move });
  latest.current = { move };
  useEffect(() => {
    const onCancel = () => void latest.current.move('cancelled');
    const onReactivate = () => void latest.current.move('draft');
    const onActivate = () => void latest.current.move('active');
    window.addEventListener(PROC_EVENTS.shipmentCancel, onCancel);
    window.addEventListener(PROC_EVENTS.shipmentReactivate, onReactivate);
    window.addEventListener(PROC_EVENTS.shipmentActivate, onActivate);
    return () => {
      window.removeEventListener(PROC_EVENTS.shipmentCancel, onCancel);
      window.removeEventListener(PROC_EVENTS.shipmentReactivate, onReactivate);
      window.removeEventListener(PROC_EVENTS.shipmentActivate, onActivate);
    };
  }, []);

  if (load.error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="this shipment" error={load.error} onRetry={load.reload} retrying={load.loading} /></div>;
  }
  if (!isNew && !shipment) {
    return <div className="wt-container fade-in"><p className="text-subtle" role="status">Loading shipment...</p></div>;
  }

  const readOnly = !canEdit;
  const err = fieldErrors.errorFor;
  const cancelled = shipment?.status === 'cancelled';
  const linkedIds = new Set((shipment?.orders ?? []).map(o => o.id));
  const orderOptions = (attachable.data ?? []).filter(o => !linkedIds.has(o.id)).map(o => ({ value: o.id, label: o.marketplace_order_id || o.seller_name || o.id.slice(0, 8).toUpperCase() }));
  const currencyOptions = [{ value: '', label: 'Choose a currency' }, ...CURRENCY_OPTIONS];

  return (
    <div className="wt-container fade-in proc-detail">
      {shipment && shipment.status === 'active' && RECEIVABLE_LEGS.includes(shipment.leg ?? '') && ARRIVED_PROGRESS.includes(shipment.progress_status) && (
        <ReadyToReceivePanel shipmentId={shipment.id} onConfirmed={() => void refreshAll()} />
      )}

      <div className="detail-layout">
        <div className="inline-form-page">
          {cancelled && (
            <div className="entity-state-banner entity-state-banner--cancelled" role="status">
              Cancelled. Its freight does not count toward landed cost. Reactivate it from the header to book it again.
            </div>
          )}
          {readOnly && (
            <div className="entity-state-banner entity-state-banner--partial" role="status">You can view this shipment but not change it.</div>
          )}

          <ProcSection title="Identity" ariaLabel="Shipment identity">
            <div className="form-row">
              <FormSelect label="Mode" value={form.mode} onChange={set('mode')} error={err('mode')} disabled={readOnly} options={[{ value: '', label: 'Not set' }, ...SHIPMENT_MODE_OPTIONS]} />
              <FormSelect label="Leg" value={form.leg} onChange={setWithAddress('leg')} error={err('leg')} disabled={readOnly} options={[{ value: '', label: 'Not set' }, ...SHIPMENT_LEG_OPTIONS]} />
              <FormSelect label="Shipping agent" value={form.shipping_agent_id} onChange={setWithAddress('shipping_agent_id')} error={err('shipping_agent_id')} disabled={readOnly} options={[{ value: '', label: 'No agent' }, ...agents.agents.map(a => ({ value: a.id, label: a.name }))]} />
            </div>
            <div className="form-row">
              <FormInput label="Waybill number" value={form.waybill_no} onChange={set('waybill_no')} error={err('waybill_no')} disabled={readOnly} />
              <FormInput label="Container reference" value={form.container_ref} onChange={set('container_ref')} error={err('container_ref')} disabled={readOnly} />
              <FormInput label="Vessel" value={form.vessel_name} onChange={set('vessel_name')} error={err('vessel_name')} disabled={readOnly} />
            </div>
            {agents.error ? <LoadErrorBox what="shipping agents" error={agents.error} onRetry={agents.reload} retrying={agents.loading} /> : null}
          </ProcSection>

          <ProcSection title="Route and dates" ariaLabel="Route and dates">
            <div className="form-row">
              <FormInput label="Port of loading" value={form.port_of_loading} onChange={set('port_of_loading')} error={err('port_of_loading')} disabled={readOnly} />
              <FormInput label="Load date" type="date" value={form.load_date} onChange={set('load_date')} error={err('load_date')} disabled={readOnly} />
              <FormInput label="Departed" type="date" value={form.actual_departure} onChange={set('actual_departure')} error={err('actual_departure')} disabled={readOnly} />
            </div>
            <div className="form-row">
              <FormInput label="Estimated arrival" type="date" value={form.estimated_arrival} onChange={set('estimated_arrival')} error={err('estimated_arrival')} disabled={readOnly} />
              <FormInput label="Arrived" type="date" value={form.actual_arrival} onChange={set('actual_arrival')} error={err('actual_arrival')} disabled={readOnly} />
            </div>
            <div className="form-row">
              <FormInput label="Notified" type="date" value={form.notified_date} onChange={set('notified_date')} error={err('notified_date')} disabled={readOnly} />
              <FormInput label="Received" type="date" value={form.received_date} onChange={set('received_date')} error={err('received_date')} disabled={readOnly} />
            </div>
          </ProcSection>

          <ProcSection title="Load and delivery" ariaLabel="Load and delivery">
            <div className="form-row">
              <FormInput label="Total cartons" inputMode="numeric" value={form.total_cartons} onChange={set('total_cartons')} error={err('total_cartons')} disabled={readOnly} />
              <FormInput label="Weight (kg)" inputMode="decimal" value={form.weight_kg} onChange={set('weight_kg')} error={err('weight_kg')} disabled={readOnly} />
              <FormInput label="Total CBM" inputMode="decimal" value={form.total_cbm} onChange={set('total_cbm')} error={err('total_cbm')} disabled={readOnly} />
            </div>
            <div className="form-row">
              <FormInput label="Delivery recipient" value={form.delivery_recipient} onChange={set('delivery_recipient')} error={err('delivery_recipient')} disabled={readOnly} />
              <FormInput label="Delivery address" value={form.delivery_address} onChange={set('delivery_address')} error={err('delivery_address')} disabled={readOnly} />
            </div>
          </ProcSection>

          {canViewFinancials && (
            <ProcSection title="Costs" ariaLabel="Shipment costs">
              <div className="form-row">
                <FormInput label="Freight" inputMode="decimal" value={form.freight_amount} onChange={set('freight_amount')} error={err('freight_amount')} disabled={readOnly} />
                <FormSelect label="Freight currency" value={form.freight_currency} onChange={set('freight_currency')} error={err('freight_currency')} disabled={readOnly} options={currencyOptions} />
                <FormInput label="Freight charged on" type="date" value={form.freight_charged_date} onChange={set('freight_charged_date')} error={err('freight_charged_date')} disabled={readOnly} />
              </div>
              <div className="form-row">
                <FormInput label="Local clearing" inputMode="decimal" value={form.local_clearing} onChange={set('local_clearing')} error={err('local_clearing')} disabled={readOnly} />
                <FormSelect label="Clearing currency" value={form.local_clearing_currency} onChange={set('local_clearing_currency')} error={err('local_clearing_currency')} disabled={readOnly} options={currencyOptions} />
              </div>
              {shipment && <p className="text-subtle">Freight estimate: {formatDecimal(shipment.freight_estimate, shipment.freight_currency)}</p>}
            </ProcSection>
          )}

          {canViewFinancials && (
            <ShippingAgentComparison agents={agents.agents} cbm={form.total_cbm} kg={form.weight_kg} selectedId={form.shipping_agent_id} />
          )}

          <ProcSection title="Notes" ariaLabel="Shipment notes">
            <FormTextarea aria-label="Notes" rows={3} value={form.notes} onChange={set('notes')} error={err('notes')} disabled={readOnly} />
          </ProcSection>

          {shipment && (
            <>
              <ProcSection title="Orders on this shipment" ariaLabel="Orders on this shipment">
                <DataTable<OrderOnShipment>
                  data={shipment.orders}
                  columns={[
                    { key: 'marketplace_order_id', header: 'Order', render: (_v, o) => (
                      <button type="button" className="link-btn" onClick={() => navigate(entityPath('procurementOrder', o.id) ?? '/procurement/orders')}>{o.marketplace_order_id || recordLabelOr('procurementOrder', o.id, o.id.slice(0, 8).toUpperCase())}</button>
                    ) },
                    { key: 'seller_name', header: 'Seller' },
                    { key: 'cbm', header: 'CBM', align: 'right' },
                    { key: 'carton_count', header: 'Cartons', align: 'right' },
                    { key: 'items', header: 'Lines', align: 'right', render: (_v, o) => o.items.length },
                    ...(canEdit ? [actionsColumn<OrderOnShipment>([{
                      key: 'unlink', icon: 'x', title: 'Unlink order',
                      onClick: o => { void (async () => { if (await run(() => detachShipment(o.id, shipment.id), 'Order unlinked', 'The order could not be unlinked.')) await refreshAll(); })(); },
                    }])] : []),
                  ]}
                  emptyMessage="No orders are linked yet."
                  compact
                />
                {canEdit && (
                  attachable.error ? <LoadErrorBox what="orders" error={attachable.error} onRetry={attachable.reload} retrying={attachable.loading} /> : (
                    <>
                      <div className="form-row">
                        <FormSelect label="Link an order" value={pickOrder} onChange={e => setPickOrder(e.target.value)} options={[{ value: '', label: orderOptions.length ? 'Choose an order' : 'No order to link' }, ...orderOptions]} />
                        <FormInput label="CBM of the order" inputMode="decimal" value={cbm} onChange={e => setCbm(e.target.value)} />
                        <FormInput label="Cartons of the order" inputMode="numeric" value={cartons} onChange={e => setCartons(e.target.value)} />
                      </div>
                      <div className="proc-section-actions">
                        <Button
                          variant="secondary" size="sm" disabled={!pickOrder}
                          onClick={async () => {
                            if (await run(() => attachShipment(pickOrder, shipment.id, { cbm: cbm.trim() || null, carton_count: cartons.trim() ? Number(cartons) : null }), 'Order linked', 'The order could not be linked.')) {
                              setPickOrder(''); setCbm(''); setCartons('');
                              await refreshAll();
                            }
                          }}
                        >Link order</Button>
                      </div>
                    </>
                  )
                )}
              </ProcSection>

              <ProcSection title="Tracking" ariaLabel="Tracking">
                <DataTable<TrackingEntry>
                  data={shipment.tracking}
                  columns={[
                    { key: 'tracking_no', header: 'Tracking number' },
                    { key: 'loaded_date', header: 'Loaded', render: (_v, t) => (t.loaded_date ? formatDate(t.loaded_date, '') || t.loaded_date : '—') },
                    { key: 'received_date', header: 'Received', render: (_v, t) => (t.received_date ? formatDate(t.received_date, '') || t.received_date : '—') },
                    { key: 'agent_status', header: 'Agent status' },
                    { key: 'last_checked', header: 'Last checked', render: (_v, t) => (t.last_checked ? formatDateTime(t.last_checked) : '—') },
                  ]}
                  emptyMessage="No tracking entries yet."
                  compact
                />
                {canEdit && (
                  <>
                    <div className="form-row">
                      <FormInput label="Waybill / tracking number" value={trackingNo} onChange={e => setTrackingNo(e.target.value)} />
                      <FormInput label="Courier" value={courier} onChange={e => setCourier(e.target.value)} />
                    </div>
                    <div className="proc-section-actions">
                      <Button
                        variant="secondary" size="sm" disabled={!trackingNo.trim()}
                        onClick={async () => {
                          if (await run(() => saveWaybill({ tracking_no: trackingNo.trim(), ...(courier.trim() ? { courier: courier.trim() } : {}), shipment_id: shipment.id }), 'Tracking number saved', 'The tracking number could not be saved.')) {
                            setTrackingNo(''); setCourier('');
                            await refreshAll();
                          }
                        }}
                      >Save tracking number</Button>
                    </div>
                  </>
                )}
              </ProcSection>

              {canViewFinancials && (
                <ProcSection title="Freight allocation" ariaLabel="Freight allocation">
                  {freight.error ? (
                    <LoadErrorBox what="the freight allocation" error={freight.error} onRetry={freight.reload} retrying={freight.loading} />
                  ) : !freight.data ? (
                    <p className="text-subtle">{freight.loading ? 'Loading freight allocation...' : 'No freight allocation yet.'}</p>
                  ) : (
                    <>
                      <p className="text-subtle">
                        Freight {formatDecimal(freight.data.freight_total, shipment.freight_currency)} and clearing {formatDecimal(freight.data.clearing_total, shipment.local_clearing_currency)}
                        {freight.data.basis ? `, split by ${freight.data.basis}` : ''}.
                      </p>
                      <DataTable<FreightShare>
                        data={freight.data.shares}
                        columns={[
                          { key: 'marketplace_order_id', header: 'Order', render: (_v, sh) => sh.marketplace_order_id || recordLabelOr('procurementOrder', sh.order_id, sh.order_id.slice(0, 8).toUpperCase()) },
                          { key: 'cbm', header: 'CBM', align: 'right' },
                          { key: 'carton_count', header: 'Cartons', align: 'right' },
                          { key: 'freight_share', header: 'Freight share', align: 'right', render: (_v, sh) => formatDecimal(sh.freight_share, shipment.freight_currency) },
                          { key: 'clearing_share', header: 'Clearing share', align: 'right', render: (_v, sh) => formatDecimal(sh.clearing_share, shipment.local_clearing_currency) },
                        ]}
                        emptyMessage="No shares yet"
                        compact
                      />
                    </>
                  )}
                </ProcSection>
              )}

              <RecordActivity
                entityType="shipment"
                entityId={shipment.id}
                extraEvents={shipment.history.map((h, i) => statusChangeEvent({
                  id: `${h.changed_at}-${i}`, fromStatus: h.from_status, toStatus: h.to_status, changedAtISO: h.changed_at,
                  source: h.source, actor: h.actor, notes: null,
                }))}
              />
            </>
          )}
        </div>

        <aside className="detail-sidebar">
          <section className="side-section">
            <h4>Shipment Summary</h4>
            <div className="stat-row"><span>Status</span><strong>{shipment ? SHIPMENT_STATUS_LABELS[shipment.status] : 'New'}</strong></div>
            {shipment && <div className="stat-row"><span>Progress</span><strong>{shipment.progress_status}</strong></div>}
            <div className="stat-row"><span>Orders</span><strong>{shipment?.orders.length ?? 0}</strong></div>
            <div className="stat-row"><span>Cartons</span><strong>{form.total_cartons.trim() || '—'}</strong></div>
            <div className="stat-row"><span>CBM</span><strong>{form.total_cbm.trim() || '—'}</strong></div>
            {canViewFinancials && shipment && (
              <div className="stat-row"><span>Freight estimate</span><strong>{formatDecimal(shipment.freight_estimate, shipment.freight_currency)}</strong></div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default ShipmentEdit;
